import { Link } from 'react-router-dom';
import { OverviewCard } from '../billing/overview/OverviewCard';
import { MyCasesList, MY_QUEUE_URL } from './MyCasesList';
import { MyConversationsList, MY_CONVERSATIONS_URL } from './MyConversationsList';
import type { ConversationRow } from '../../services/conversations';
import type { WorkQueueCase } from '../../services/workQueue';

// The two "about me" cards across the top of Home: side by side when wide, stacked when narrow.

// Shared by both list cards' headers.
const CARD_TITLE = 'text-base';

function HeaderLink({
    to,
    label,
    children
}: {
    to: string;
    label?: string;
    children: React.ReactNode;
}) {
    return (
        <Link
            to={to}
            aria-label={label}
            className="shrink-0 text-[12px] font-semibold text-gray-500 hover:text-gray-800 transition-colors"
        >
            {children}
        </Link>
    );
}

interface HomeSummaryRowProps {
    cases: WorkQueueCase[];
    casesTotal: number;
    casesLoading: boolean;
    casesError: boolean;
    conversations: ConversationRow[];
    conversationsLoading: boolean;
    conversationsError: boolean;
    className?: string;
}

export function HomeSummaryRow({
    cases,
    casesTotal,
    casesLoading,
    casesError,
    conversations,
    conversationsLoading,
    conversationsError,
    className = ''
}: HomeSummaryRowProps) {
    return (
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${className}`}>
            <OverviewCard
                title="My cases"
                titleClassName={CARD_TITLE}
                info="Cases assigned to you, highest priority first and oldest first within each priority band. Every row opens the case in the Work Queue."
                noPadding
                className="h-full min-h-0 flex flex-col"
                contentClassName="flex-1 min-h-0 overflow-y-auto scrollbar-hide"
                action={<HeaderLink to={MY_QUEUE_URL}>View in Work Queue</HeaderLink>}
            >
                {casesError ? (
                    <p className="text-sm text-gray-400 py-16 text-center">
                        Couldn&apos;t load your cases.
                    </p>
                ) : (
                    <MyCasesList cases={cases} total={casesTotal} isLoading={casesLoading} />
                )}
            </OverviewCard>

            <OverviewCard
                title="My conversations"
                titleClassName={CARD_TITLE}
                info="Unread patient conversations, newest first: yours plus any nobody has claimed on your team. Every row opens that conversation in Conversations."
                noPadding
                className="h-full min-h-0 flex flex-col"
                contentClassName="flex-1 min-h-0 overflow-y-auto scrollbar-hide"
                action={
                    <HeaderLink to={MY_CONVERSATIONS_URL} label="View all conversations">
                        View all
                    </HeaderLink>
                }
            >
                {conversationsError ? (
                    <p className="text-sm text-gray-400 py-16 text-center">
                        Couldn&apos;t load your conversations.
                    </p>
                ) : (
                    <MyConversationsList
                        conversations={conversations}
                        isLoading={conversationsLoading}
                    />
                )}
            </OverviewCard>
        </div>
    );
}
